/**
 * @author Muhammad Fahad
 * @datetime April, 1st 2022
 * @description: This is main class/component of application. This component mounts
 *                before everyother component. This component also handles operations
 *                like show & hiding header etc.
 */
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { BRANDS, LANGUAGES } from './shared/models/screen-size.enum';
import { StateManagementService } from './shared/services/state-management.service';
import { HelperService } from './shared/utils/helper.service';
import './shared/globals/globals';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Router } from '@angular/router';
import { UserLoginService } from './shared/services/user-login.service';
import { MatDialogRef } from '@angular/material/dialog';
import { LoaderService } from './shared/utils/loader.service';
import { environment } from 'src/environments/environment';
import { AlertDialogComponent } from './shared-layout/Components/alert-dialog/alert-dialog.component';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { CONSTANTS } from './shared/constants/constants';
import { AccountSettingsService } from './shared-layout/header/account-settings/account-setting.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { NgRedux } from '@angular-redux/store';
import { IAppState } from './shared/state-management/state-interfaces';
import { IUser } from './user-management/user-management-interfaces';
import LoginRoutingPath from './login/login-routing-path';
import { LocalizationService } from './shared/services/localization.service';
import { Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { DrAuthService } from 'src/app/shared/utils/dr-auth-service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  idleState = 'Not started.'; // console message
  timedOut = false;
  lastPing?: Date | null = null;
  dialogRef: MatDialogRef<AlertDialogComponent> | undefined;
  alreadyLoggedIn: boolean = false; // local variable
  private screenTimeout = environment.totalDelayTimeOut;
  private WarningDelay = environment.delayWarningTime;
  CONSTANTS = CONSTANTS;
  user!: Observable<IUser | undefined>;

  title = 'MultiFamily Frontend';
  subscription!: Subscription;
  headerSubject$ = new BehaviorSubject<boolean>(false);
  isInactivityModalOpened = false;
  constructor(
    private stateManagementService: StateManagementService,
    private helperService: HelperService,
    private changeDetectorRef: ChangeDetectorRef,
    public idle: Idle,
    private router: Router,
    private userService: UserLoginService,
    private loaderService: LoaderService,
    private accountSettingService: AccountSettingsService,
    public sharedDataService: SharedDataService,
    private ngRedux: NgRedux<IAppState>,
    private localizationService: LocalizationService,
    private meta: Meta,
    private translate: TranslateService,
    private drAuth: DrAuthService
  ) {
    this.user = this.ngRedux.select('user');
    this.drAuth.init();
    this.setNoIndexTag();
  }

  /**
   * @description: runs on when component is initialized. Subscribe the store through user management service
   * @input {none}
   * @output {void}
   */
  ngOnInit(): void {
    this.autoLogoutConfigure(); // register auto login feauture
    const time = this.stateManagementService.getLastActivityTime();
    const screenTimeoutMS = this.screenTimeout * 1000;
    if (time && screenTimeoutMS < Date.now() - time) {
      this.logout();
    }

    this.listenUserActivity();

    // the user management service subscribes the store
    this.subscription = this.stateManagementService?.subscribe(() => {
      let headerState = this.stateManagementService?.getShowHeader();
      let isLoggedId = this.stateManagementService?.getLoggedIn();
      let user = this.stateManagementService?.getUser();
      //updated value is emitted

      this.headerSubject$.next(headerState);

      // whenever state get isLoggedId true and alreadyLoggedIn is false that means user come from first time
      if (isLoggedId && !this.alreadyLoggedIn) {
        this.isLoggedInStatus(true);
        this.alreadyLoggedIn = isLoggedId;
      }

      // whenever state get isLoggedId false and alreadyLoggedIn is true that means state change from store
      else if (!isLoggedId && this.alreadyLoggedIn) {
        this.isLoggedInStatus(false);
        this.alreadyLoggedIn = isLoggedId;
      }

      // the purpose for changeDetectorRef is to communicate with parent and child relationship
      this.changeDetectorRef.detectChanges();
    });

    const url = window.location.href;
    if (window.__dev__) console.log('URL', url);
    let brandName = BRANDS.KWIKSETUNITE;
    // let language = LANGUAGES.ENGLISH;
    if (url.includes(BRANDS.KWIKSETUNITE)) {
      brandName = BRANDS.KWIKSETUNITE;
    } else if (url.includes(BRANDS.WEISER)) {
      brandName = BRANDS.WEISER;
    }
    if (window.__dev__) console.log('brandName', brandName);
    document.title =
      brandName === BRANDS.KWIKSETUNITE
        ? CONSTANTS.KWIKSET_UNITE_TITLE
        : CONSTANTS.WEISER_UNITE_TITLE;
    this.stateManagementService.updateUserBrand(brandName);
    // this.stateManagementService.updateUserLanguage(language);
    // this.translate.setDefaultLang(language);
    // this.translate.use(language);
    // document.documentElement.setAttribute('lang', language);
    this.localizationService.setupLocalization();

    this.helperService.setTheme(brandName);
    const faviconPath =
      brandName === BRANDS.KWIKSETUNITE
        ? CONSTANTS.KWIKSET_UNITE_FAVICO
        : CONSTANTS.WEISER_UNITE_FAVICO;
    this.setFavicon(faviconPath);
    const description =
      brandName === BRANDS.KWIKSETUNITE
        ? CONSTANTS.KWIKSET_UNITE_DESCRIPTION
        : CONSTANTS.WEISER_UNITE_DESCRIPTION;
    this.setMetaDescription(description);
  }

  /**
   * Dynamically sets the favicon.
   * @param href - The path to the favicon.
   */
  setFavicon(href: string): void {
    let link: HTMLLinkElement | null =
      document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/x-icon';
    link.href = href;
  }

  /**
   * Dynamically sets the meta description.
   * @param content - The content for the meta description.
   */
  setMetaDescription(content: string): void {
    let meta: HTMLMetaElement | null = document.querySelector(
      'meta[name="description"]'
    );
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  /**
   * @description  subscription if user loggIn restart time
   * @param status boolean status
   * @returns void
   */
  isLoggedInStatus(status: boolean): void {
    if (window.__dev__) console.log('isLoggedInStatus', status);
    if (status) {
      this.idle.watch();
      this.timedOut = false;
    } else {
      this.idle.stop();
    }
  }

  /**
   * @description auto logout while screen Idle for given time frame
   * show model popup after seen inactivity warning given time user session clear and logout
   * @returns void
   */
  autoLogoutConfigure(): void {
    // sets an idle timeout of {{given}} minutes.
    this.idleState = 'execute';
    if (window.__dev__) console.log('autoLogoutConfigure', this.idleState);
    this.idle.setIdle(this.screenTimeout);
    // sets a timeout period of {{given}} minutes. after {{given}} minutes of inactivity, the user will be considered timed out.
    this.idle.setTimeout(this.WarningDelay);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // idle time start and find any activty idle time reset
    this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle.';
      if (window.__dev__) console.log('onIdleEnd', this.idleState);
      this.reset();
    });

    // timeout session while no longer activity
    this.idle.onTimeout.subscribe(() => {
      this.dialogRef?.close(false); // false for not stay login
      this.idleState = 'Timed out!';
      this.timedOut = true;
      if (window.__dev__) console.log('onTimeout', this.idleState);
      this.logout();
    });

    // show model popup after adle start
    this.idle.onIdleStart.subscribe(() => {
      this.idleState = "You've gone idle!";
      if (window.__dev__) console.log('onIdleStart', this.idleState);
      this.openConfirmDialogue();
    });

    // after show popup warning time start
    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!';
      if (window.__dev__) console.log('onTimeoutWarning', this.idleState);
    });
  }
  /**
   * @description open dialog model
   * @returns void
   */
  openConfirmDialogue(): void {
    if (!this.dialogRef) {
      this.isInactivityModalOpened = true;
      this.dialogRef = this.helperService.openModalDialog(
        AlertDialogComponent,
        {
          title: this.translate.instant('ABOUT_TO_LOGOUT'),
          bodyText: this.translate.instant(
            'YOU_ARE_ABOUT_TO_LOGOUT_FOR_INACTIVITY'
          ),
          btnOk: this.translate.instant('STAY_LOGGED_IN'),
          crossIcon: false,
        },
        CONSTANTS.FIXED_SCREEN_MODAL,
        false
      );
      this.dialogRef?.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.stay();
        } else {
          window.location.href = window.location.origin + '/login';
          this.sharedDataService.resetSharedData();
          this.dialogRef = undefined;
        }
      });
    }
  }

  /**
   * @description reset idle watch and restart timer
   * @returns void
   */
  reset(): void {
    this.idle.watch();
    this.timedOut = false;
    this.stateManagementService.updateLastActivity();
  }

  /**
   * @description keep login and model close
   * @returns void
   */
  stay() {
    this.dialogRef = undefined;
    this.reset();
  }

  /**
   * @description remove session and logout
   * @returns void
   */
  logout(): void {
    this.loaderService.show();
    this.helperService.closeAllMatDialog();
    this.userService.logout().subscribe({
      next: () => {
        this.loaderService.hide();
        this.dialogRef?.close(false); // false for not stay login
      },
      error: () => {
        this.loaderService.hide();
        this.dialogRef?.close(false); // false for not stay login
      },
    });
  }

  resetTimeoutTimer() {
    this.stateManagementService.updateLastActivity();
  }

  listenUserActivity() {
    document.addEventListener('touchstart', (event) => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    document.addEventListener('wheel', () => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    document.addEventListener('keypress', () => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    document.addEventListener('mouseup', () => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    document.addEventListener('mousedown', () => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    document.addEventListener('mousemove', () => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    document.addEventListener('scroll', () => {
      if (!this.isInactivityModalOpened) {
        this.resetTimeoutTimer();
      }
    });
    this.resetTimeoutTimer();
  }

  /**
   * @description: runs just before component unmount. Unsubscribe the store through subscription instance
   * @input {none}
   * @output {void}
   */
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private setNoIndexTag(): void {
    const hostname = window.location.hostname;

    // Define conditions for non-production environments
    const isProduction =
      hostname === CONSTANTS.KWIKSET_UNITE_UI_PROD_URL ||
      hostname === CONSTANTS.WEISER_UNITE_UI_PROD_URL;

    if (!isProduction) {
      this.meta.addTag({ name: 'robots', content: 'noindex' });
    }
  }
}
